// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle, { SectionTitleStyles } from "@werner/App/Components/Common/SectionTitle";
import SalesPersonCard, { SalesPersonCardStyles } from "@werner/App/Components/MiniMyAccount/SalesPersonCard";
import {
    checkoutReviewLabel,
    checkoutReviewLabelValueWrapper,
    checkoutReviewValue,
} from "@werner/App/SharedStyle/CheckoutPageStyles";
import orderDetailsSharedStyles from "@werner/App/SharedStyle/OrderDetailsSharedStyles";
import { SalesPerson } from "@werner/App/Store/Types/SalesPerson";
import { mapApiFieldsToUiFields } from "@werner/XDL/Utilities";
import React, { useContext } from "react";
import { css } from "styled-components";

const enum fields {
    showOrderBy = "showOrderBy",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showOrderBy]: boolean;
    };
}

export interface OrderDetailsSalesPersonStyles {
    wrapper?: InjectableCss;
    sectionTitle?: SectionTitleStyles;
    orderByWrapper?: InjectableCss;
    orderByLabel?: TypographyProps;
    orderByValue?: TypographyProps;
    salesPersonStyles?: SalesPersonCardStyles;
}

export const orderDetailsSalesPersonStyles: OrderDetailsSalesPersonStyles = {
    wrapper: {
        css: css`
            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    sectionTitle: {
        ...orderDetailsSharedStyles.sectionTitle,
    },
    orderByWrapper: { ...checkoutReviewLabelValueWrapper },
    orderByLabel: { ...checkoutReviewLabel },
    orderByValue: { ...checkoutReviewValue },
    salesPersonStyles: {
        wrapper: {
            css: css`
                margin-bottom: 16px;
            `,
        },
    },
};

const styles = orderDetailsSalesPersonStyles;

const apiToUiKeys: { [key: string]: keyof SalesPerson } = {
    Name: "name",
    Email: "email",
    Phone: "phone",
};

const OrderDetailsSalesPerson: React.FunctionComponent<OwnProps> = ({ fields: { showOrderBy } }) => {
    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }

    const insideSalesPerson = mapApiFieldsToUiFields<SalesPerson>(
        JSON.parse(order.properties?.insideSalesPerson || "{}"),
        apiToUiKeys,
    );
    const outsideSalesPerson = mapApiFieldsToUiFields<SalesPerson>(
        JSON.parse(order.properties?.outsideSalesPerson || "{}"),
        apiToUiKeys,
    );
    const orderBy = order.properties.orderBy;
    const isShareEntity = getIsShareEntity();

    return (
        <StyledWrapper {...styles.wrapper}>
            <SectionTitle
                title={translate("Sales Person")}
                extendedStyles={styles.sectionTitle}
                className={isShareEntity ? "share-entity-section-title" : ""}
            />
            {!!(showOrderBy && orderBy) && (
                <StyledWrapper {...styles.orderByWrapper}>
                    <Typography {...styles.orderByLabel}>{translate("Order By")}</Typography>
                    <Typography {...styles.orderByValue}>{orderBy}</Typography>
                </StyledWrapper>
            )}
            <SalesPersonCard
                salesPerson={insideSalesPerson}
                salesPersonType="inside"
                extendedStyles={styles.salesPersonStyles}
            />
            <SalesPersonCard
                salesPerson={outsideSalesPerson}
                salesPersonType="outside"
                extendedStyles={styles.salesPersonStyles}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsSalesPerson,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.showOrderBy,
                defaultValue: true,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
